import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { NextUp } from "@/components/Callouts";
import { circleIndex } from "@/theory/notes";

// Enharmonic-tolerant: idx("Bb") and idx("Bb") find the same seat.
const idx = circleIndex;

export default function MusicTheory12() {
  const { lesson, course, accent } = findLesson("music-theory", "12");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT12-Scale-Memory-Animated.mp4"
        caption="Memory tricks — formula, order of sharps, fifth-step rule"
        hint="≈ 22 sec · watch first"
      />
      <Chapter number="12" eyebrow="Music Theory · Chapter Twelve" title="Three lenses for remembering the major scales">
        <Prose>
          <p>
            By now you've seen all twelve major scales at least twice — once via the
            tone-and-semitone recipe in Chapter 1, and again via the circle of fifths in
            the last two chapters. Knowing the scales, however, is not the same as
            <em> remembering</em> them under pressure: in the middle of a song, on a stage,
            in the car, brushing your teeth. This chapter is about the muscle-memory
            tricks that move the scales out of the textbook and into the body.
          </p>
          <p>
            Three lenses cover almost everyone. The first is <strong>shape</strong> — the
            piano-worm pattern that each scale traces across the keys. The second is
            <strong> rule of accidentals</strong> — the fact that a sharp destroys the
            white key on its left and a flat destroys the one on its right, which gives
            you a built-in error-check. The third is <strong>circle position</strong> —
            using the count from C around the circle to know how many sharps or flats
            the scale carries before you've spelled a single note. Apply all three and
            the twelve scales stop being a memory load.
          </p>
        </Prose>
        <MTQuote>
          Remember your piano worms, visualize them to a point that even when you're, I
          guess, brushing your teeth or just about waking up or in a car, you know, stuck in
          metro traffic, you could just visualize scales.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/12a-forming-A-major-3-step-trick.png"
          caption="Jason's 3-step trick. Step 1: write the seven letters starting on your tonic. Step 2: read the key signature off the circle of fifths (3 sharps for A major, 2 flats for B♭ major). Step 3: pencil the sharps/flats onto the right letters. No more counting tones and semitones — just look up and write."
        />
      </Chapter>

      <Chapter eyebrow="Lens one" title="Piano worms — every scale has a shape">
        <Prose>
          <p>
            Each major scale traces a distinctive cluster of black and white keys across
            an octave. We call those clusters <em>piano worms</em>. Once you've drawn a
            scale's worm a few times, you can recall it the way you recall a logo or a
            constellation — as a single image, not a sequence of seven moves.
          </p>
          <p>
            Take E major as an example: E F♯ G♯ A B C♯ D♯. The worm has two pairs of
            black keys with a single white between them, then a longer white stretch, and
            then another two black keys near the top — almost like two boats turned
            upside down, the same size, sitting next to each other on the keyboard.
            Picture that and you'll never lose E major again.
          </p>
          <p>
            A bonus pattern: a key and its <em>flat counterpart</em> are inverses of each
            other. The worm of E major is the visual mirror of E♭ major. Same with B
            major and B♭ major, F♯ major and F major, A♭ major and A major. If you've
            mastered one, the other comes free — just flip the shape.
          </p>
        </Prose>

        <TheoryBox label="Train the eye, free the hand">
          Draw the piano worm for each scale on paper or in a notebook. Then close your
          eyes and play the scale ascending and descending, picturing the shape. Repeat
          until you can do it without looking at the keys. That's when the scale has
          truly entered the body.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Lens two" title="Sharps and flats — what they actually destroy">
        <Prose>
          <p>
            A sharp by nature <strong>removes the white key to its left</strong>. F♯
            destroys F. C♯ destroys C. G♯ destroys G. Every time you write a sharp into a
            scale, you're saying "do not play the natural-letter version of this note —
            play one semitone above it instead." That gives you a built-in self-check:
            an A major scale has C♯, F♯, G♯, which means the scale cannot contain C, F or
            G as natural notes anywhere. If you find one, you've made an error.
          </p>
          <p>
            A flat by nature <strong>removes the white key to its right</strong>. B♭
            destroys B. E♭ destroys E. A♭ destroys A. So in E♭ major (B♭, E♭, A♭) there
            is no plain E, no plain A, no plain B in the scale. Saying the flats out
            loud while you play — "no E, no A, no B" — keeps your fingers honest while
            the worm is still settling in.
          </p>
        </Prose>

        <TheoryBox label="The destroy-rule">
          A <strong>sharp</strong> removes the white key on its left.
          A <strong>flat</strong> removes the white key on its right.
          Use this as a continuous error-check: a sharp scale will never contain the
          natural-letter version of any of its sharps. A flat scale will never contain
          the natural-letter version of any of its flats.
        </TheoryBox>

        <Prose>
          <p>
            This is also why we never mix sharps and flats inside a single major scale.
            E major is spelled with C♯, F♯, G♯, D♯ — not with D♭, F♯, G♭ and friends.
            Mixing them breaks the seven-letter rule (every letter A–G must appear
            exactly once) and creates absurd spellings like A B D♭ D E F♯ G♯, which has
            two D's and no C. Pick a side; the circle tells you which one.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Lens three" title="The circle gives you the count">
        <Prose>
          <p>
            The third lens is the one we built in Chapters 10 and 11. The position of a
            scale on the circle of fifths tells you, before you've written a single note,
            how many sharps or flats it carries — and therefore how much "work" is
            involved in spelling it out.
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths
            size={340}
            highlights={[
              { index: idx("C"),  color: "#94a3b8", label: "0" },
              { index: idx("G"),  color: "#f59e0b", label: "1♯" },
              { index: idx("D"),  color: "#f59e0b", label: "2♯" },
              { index: idx("A"),  color: "#f59e0b", label: "3♯" },
              { index: idx("E"),  color: "#f59e0b", label: "4♯" },
              { index: idx("B"),  color: "#f59e0b", label: "5♯" },
              { index: idx("F#"), color: "#f59e0b", label: "6" },
              { index: idx("F"),  color: "#a855f7", label: "1♭" },
              { index: idx("Bb"), color: "#a855f7", label: "2♭" },
              { index: idx("Eb"), color: "#a855f7", label: "3♭" },
              { index: idx("Ab"), color: "#a855f7", label: "4♭" },
              { index: idx("Db"), color: "#a855f7", label: "5♭" },
            ]}
          />
        </div>

        <Prose>
          <p>
            Workflow for any major scale, in three quick steps:
          </p>
          <ol className="ml-6 list-decimal space-y-1 text-[14px]">
            <li>
              Locate the tonic on the circle. Count the steps from C — clockwise gives
              sharps, counter-clockwise gives flats.
            </li>
            <li>
              That step-count is the number of accidentals. Pull them off the order of
              sharps (F♯ C♯ G♯ D♯ A♯ E♯ B♯) or the order of flats (B♭ E♭ A♭ D♭ G♭ C♭ F♭).
            </li>
            <li>
              Write the seven letters A–G starting at the tonic, then apply the
              accidentals to whichever letters they belong to. Done.
            </li>
          </ol>
          <p>
            Try it on A major. A is three steps clockwise from C, so A major has three
            sharps; the first three from the order are F♯, C♯, G♯. Write A B C D E F G —
            then sharpen the C, F and G. Result: A B C♯ D E F♯ G♯. No tone-and-semitone
            counting required.
          </p>
        </Prose>

        <ScaleExplorer tonic="A" scales={["major"]} />
      </Chapter>

      <Chapter eyebrow="Drilling the relative minors" title="A free pattern hidden inside the majors">
        <Prose>
          <p>
            Each major scale comes with a relative minor — a scale built on the major's
            sixth degree, sharing the same key signature. C major's relative minor is A
            minor (A is the sixth note of C major). G major's relative minor is E minor.
            D major's is B minor. And so on, in fifths.
          </p>
          <p>
            There's no clever shortcut for "what's C's relative minor"? You just memorise
            "C major's relative minor is A minor" and let the rest follow in fifths.
            The relative minors share the same key signature as their majors, which is
            the most important fact for sight-reading: a piece with one sharp at the
            start of the staff is in either G major or E minor, and you decide which by
            looking at where the music begins and ends.
          </p>
          <p>
            Don't try to mug up modes (Dorian, Phrygian, Lydian, Mixolydian) until the
            twelve majors and their relative minors feel automatic. Once the foundation
            is solid, the modes become a one-line edit on top.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A daily practice" title="Three minutes a day, twelve weeks">
        <Prose>
          <p>
            Pick one scale per day. Draw its worm on paper. Recite its key signature.
            Play it ascending and descending with eyes closed, calling each note name out
            loud. Then play it as a melody — make up a four-bar tune that uses only that
            scale. Move to the next one tomorrow.
          </p>
          <p>
            Twelve days for the round trip. Repeat that round trip seven times and the
            scales become invisible — you'll be thinking about phrases and shapes
            instead of "what comes after the third degree of B♭ major". That's when the
            real composition work starts.
          </p>
        </Prose>

        <Glossary>
          <Term name="Piano worm" def="The visual cluster of black and white keys that a scale traces across one octave of the keyboard." />
          <Term name="Inverse worm" def="A scale and its enharmonic flat counterpart (E ↔ E♭, A ↔ A♭, B ↔ B♭, F ↔ F♯) trace mirrored shapes — once you know one, the other is free." />
          <Term name="Relative minor" def="The minor scale that shares a key signature with its major. Built on the sixth degree of the major. C ↔ Am, G ↔ Em, F ↔ Dm, etc." />
          <Term name="Destroy-rule" def="A sharp removes the white key on its left; a flat removes the white key on its right. A built-in error-check while you play." />
        </Glossary>
        <NoteImage
          src="/notes/music-theory/finals/12b-scales-with-opposite-shapes.png"
          caption="Two scales from opposite sides of the circle of fifths produce mirror-image piano-worm shapes. D major and D♭ major. C major and C♭ major. A major and A♭ major. The shape collapse is a beautiful side-effect of the circle's symmetry."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-13"
        label="Chapter 13 · Circle of Fifths for Intervals"
        why="With every scale memorable, the next move is to use the circle to read intervals at sight — and to feel why distant notes sound dissonant and close ones sound at home."
      />
    </LessonShell>
  );
}
